import React, { useState, useEffect } from 'react';
import axiosInstance from "../config/axiosConfig";
import BuscarPaciente from "./components/BuscarPaciente";
import BuscarAuxiliar from "./components/BuscarAuxiliar";
import { formatDate } from "../utils/utils";
import { cleanRut, validateRut, formatRut } from '../utils/rut';
import Select from 'react-select';
/* global gdAlertify */

const NuevoAcuerdoMutuo2 = () => {
    const [paciente, setPaciente] = useState(null);
    const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
    const [slots, setSlots] = useState([]);
    const [auxiliarActual, setAuxiliarActual] = useState(null);
    const [slotsSeleccionados, setSlotsSeleccionados] = useState([]);
    const [slotsAsignados, setSlotsAsignados] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showProyeccion, setShowProyeccion] = useState(false);
    const [observacion, setObservacion] = useState("");

    // ==========================================
    // ESTADOS PARA MODIFICACIÓN DE ROTACIÓN
    // ==========================================
    const [showRotationModal, setShowRotationModal] = useState(false);
    const [patronesTemplates, setPatronesTemplates] = useState([]);
    const [templateSelected, setTemplateSelected] = useState(null);
    
    // Estados del formulario de rotación (Similares a EditarPaciente.js)
    const [edt_fechaInicioServicio, setEdt_fechaInicioServicio] = useState("");
    const [edt_serviceDays, setEdt_serviceDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
    const [edt_patternType, setEdt_patternType] = useState("fixed");
    const [edt_fixedPeriod, setEdt_fixedPeriod] = useState("day");
    const [edt_patternStartTime, setEdt_patternStartTime] = useState("");
    const [edt_patternEndTime, setEdt_patternEndTime] = useState("");
    const [edt_patternRows, setEdt_patternRows] = useState([]);
    const [edt_diasOrdenadosUI, setEdt_diasOrdenadosUI] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
    
    const daysMap = [
        { value: 'Mon', label: 'Lunes' },
        { value: 'Tue', label: 'Martes' },
        { value: 'Wed', label: 'Miércoles' },
        { value: 'Thu', label: 'Jueves' },
        { value: 'Fri', label: 'Viernes' },
        { value: 'Sat', label: 'Sábado' },
        { value: 'Sun', label: 'Domingo' }
    ];

    // Valores por defecto
    const [valoresBase, setValoresBase] = useState({
        valorTurno: "",
        valorMovilizacion: "",
        valorColacion: "",
        tipoColacion: "3", // 1=Día, 2=Noche, 3=Ambos
        tipoMovilizacion: "3" // 1=Día, 2=Noche, 3=Ambos
    });
    
    // Cargar plantillas de patrones
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const response = await axiosInstance.get("/patrones-rotacion/listar");
                if (!response.data.error) {
                    setPatronesTemplates(response.data.data.map(p => ({
                        value: p.id,
                        label: `${p.titulo} (${p.cantidad_auxiliares || 1} Auxiliares)`,
                        data: p
                    })));
                }
            } catch (error) {
                console.error("Error loading templates:", error);
            }
        };
        fetchTemplates();
    }, []);

    // ========== SELECCIÓN DE PACIENTE ==========
    const handleSelectPaciente = async (p) => {
        if (!p) {
            setPaciente(null);
            setSlots([]);
            setAuxiliarActual(null);
            setSlotsSeleccionados([]);
            setSlotsAsignados([]);
            return;
        }

        gdAlertify.showLoader();
        try {
            const response = await axiosInstance.get("/paciente/busqueda", { params: { id: p.id } });
            const fullData = response.data.data[0] || {};
            setPaciente(fullData);

            // Cargar valores base
            if (fullData.valoresServicio && fullData.valoresServicio.length > 0) {
                const vs = fullData.valoresServicio[0];
                setValoresBase({
                    valorTurno: vs.valorTurnoAuxiliar || 0,
                    valorMovilizacion: vs.valorLocomocionAuxiliar || 0,
                    valorColacion: vs.valorColacionAuxiliar || 0,
                    tipoColacion: "3"
                });
            }

            // Generar slots
            if (fullData.turnos) {
                const fechaReferenciaServicio = obtenerFechaInicioServicio(fullData);
                
                // Actualizar la fecha de inicio con la del servicio del paciente
                setFechaInicio(fechaReferenciaServicio);
                
                // Usamos fechaReferenciaServicio para generar los slots iniciales también
                // Nota: generarSlots usa fechaInicio que es un estado, pero aquí lo pasamos explícitamente
                // para asegurar consistencia inmediata sin esperar al re-render
                
                // IMPORTANTE: generarSlots usa 'fechaInicio' (2do arg) para visualizar y 'fechaReferenciaServicio' (3er arg) para calcular
                // Al igualar ambos, la primera visualización partirá desde el inicio del servicio
                const slotsGenerados = generarSlots(fullData.turnos, fechaReferenciaServicio, fechaReferenciaServicio);
                setSlots(slotsGenerados);
            }

        } catch (error) {
            console.error(error);
            gdAlertify.notiError("Error al cargar paciente");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    // ========== OBTENER FECHA INICIO SERVICIO ==========
    const obtenerFechaInicioServicio = (pacienteData) => {
        if (pacienteData.tutores && pacienteData.tutores.length > 0) {
            const tutorConFecha = pacienteData.tutores.find(t => t.fechaInicioServicio);
            if (tutorConFecha && tutorConFecha.fechaInicioServicio) {
                return tutorConFecha.fechaInicioServicio;
            }
        }
        return pacienteData.inicioRealServicio || new Date().toISOString().split('T')[0];
    };

    // ========== GENERAR SLOTS ==========
    const generarSlots = (turno, fechaInicioStr, fechaReferenciaServicio) => {
        if (!turno) return [];

        if (turno.type === "fixed") {
            return generarSlotsFixed(turno, fechaInicioStr);
        } else if (turno.type === "rotative") {
            return generarSlotsRotative(turno, fechaInicioStr, fechaReferenciaServicio);
        }

        return [];
    };

    // ========== GENERAR SLOTS PARA PATRÓN FIJO ==========
    const generarSlotsFixed = (turno, fechaInicioStr) => {
        const slotsTemp = [];
        const daysService = turno.days_service ? turno.days_service.split(',') : [];

        if (daysService.length === 0) return slotsTemp;

        // Mapa para nombres de días
        const dayMap = {
            'Mon': 'Lunes',
            'Tue': 'Martes',
            'Wed': 'Miércoles',
            'Thu': 'Jueves',
            'Fri': 'Viernes',
            'Sat': 'Sábado',
            'Sun': 'Domingo'
        };

        // Generar texto resumen de días (ej: "Lunes, Miércoles, Viernes")
        const diasTexto = daysService.map(d => dayMap[d] || d).join(', ');
        const fechaInicio = new Date(fechaInicioStr);

        // Crear UN solo slot que representa todo el turno fijo
        slotsTemp.push({
            id: 0,
            slotIndex: 0,
            patternSlotOriginal: 0,
            dias: 1, // Valor nominal
            periodo: turno.period || 'day',
            status: 'libre', // Se asume libre inicial, validación externa
            fechaInicio: fechaInicio,
            fechaFin: fechaInicio,
            diaSemana: diasTexto, // Mostrar todos los días en la tarjeta
            disponible: true,
            esRecurrente: true,
            horaInicio: turno.hora_inicio || '',
            horaFin: turno.hora_fin || ''
        });

        return slotsTemp;
    };

    // ========== GENERAR SLOTS PARA PATRÓN ROTATIVO ==========
    /**
     * Para patrón rotativo: Mostrar SOLO los slots definidos en el patrón.
     * Si el patrón tiene 2 slots, mostrar 2 slots (no ciclos múltiples).
     * Calcular la fecha de inicio de cada slot basándose en fechaInicioServicio.
     */
    const generarSlotsRotative = (turno, fechaInicioStr, fechaReferenciaServicio) => {
        const slotsTemp = [];

        if (!turno.rotation_pattern) return slotsTemp;

        try {
            let pattern = typeof turno.rotation_pattern === "string"
                ? JSON.parse(turno.rotation_pattern)
                : turno.rotation_pattern;

            if (!Array.isArray(pattern)) pattern = [pattern];

            // Calcular el ciclo completo del patrón
            const cicloCompleto = pattern.reduce((sum, p) => sum + (parseInt(p.days) || 0), 0);

            if (cicloCompleto === 0) return slotsTemp;

            // Calcular días desde la fecha de referencia hasta la fecha de inicio
            // Usamos T12:00:00 para evitar problemas de timezone que cambien el día
            const fechaRef = new Date(fechaReferenciaServicio + 'T12:00:00');
            const fechaIni = new Date(fechaInicioStr + 'T12:00:00');
            const diasDesdeReferencia = Math.floor((fechaIni - fechaRef) / (1000 * 60 * 60 * 24));

            // Calcular en qué posición del ciclo estamos
            const posicionEnCiclo = ((diasDesdeReferencia % cicloCompleto) + cicloCompleto) % cicloCompleto;

            // Encontrar el slot actual y cuántos días llevamos dentro de él
            let diasAcumulados = 0;
            let slotInicial = 0;
            let diasDentroDelSlot = 0;

            for (let i = 0; i < pattern.length; i++) {
                const diasDelSlot = parseInt(pattern[i].days) || 0;
                if (diasAcumulados + diasDelSlot > posicionEnCiclo) {
                    slotInicial = i;
                    diasDentroDelSlot = posicionEnCiclo - diasAcumulados;
                    break;
                }
                diasAcumulados += diasDelSlot;
            }

            // Generar SOLO los slots del patrón (un ciclo completo)
            let currentDate = new Date(fechaIni);

            // Retroceder al inicio del slot actual si estamos en medio de él
            if (diasDentroDelSlot > 0) {
                currentDate.setDate(currentDate.getDate() - diasDentroDelSlot);
            }

            // Generar cada slot del patrón
            for (let i = 0; i < pattern.length; i++) {
                const slotIndexEnPatron = (slotInicial + i) % pattern.length;
                const p = pattern[slotIndexEnPatron];
                const diasTotal = parseInt(p.days) || 1;
                const periodo = p.period || 'day';
                const status = p.status || 'libre';

                // Fechas REALES del ciclo (ej: empieza el 12)
                const cycleStartDate = new Date(currentDate);
                const cycleEndDate = new Date(currentDate);
                cycleEndDate.setDate(cycleEndDate.getDate() + diasTotal - 1);

                // Determinar qué mostrar al usuario
                let visibleStartDate = new Date(cycleStartDate);
                let visibleDays = diasTotal;
                let isCut = false;

                // Si es el PRIMER slot y estamos "dentro" de él (recorte al inicio)
                if (i === 0 && diasDentroDelSlot > 0) {
                    // El usuario ve su fecha de inicio, no la del ciclo
                    visibleStartDate = new Date(fechaInicioStr + 'T12:00:00');
                    visibleDays = diasTotal - diasDentroDelSlot;
                    isCut = true;
                }

                const diaSemana = new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(visibleStartDate);

                // Si por algún motivo visibleDays <= 0 (ej: empezó justo al final), lo manejamos (aunque la lógica de slotInicial debería evitarlo)
                if (visibleDays > 0) {
                    slotsTemp.push({
                        id: i,
                        slotIndex: slotIndexEnPatron,
                        patternSlotOriginal: slotIndexEnPatron,

                        // Datos Visuales (Recortados si aplica)
                        dias: visibleDays,
                        fechaInicio: visibleStartDate,
                        fechaFin: cycleEndDate, // El fin siempre es el fin del ciclo

                        // Datos Originales (Contexto)
                        originalDias: diasTotal,
                        originalFechaInicio: cycleStartDate,
                        esRecortado: isCut,

                        periodo: periodo,
                        status: status,
                        diaSemana: diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1),
                        disponible: true,
                        esRecurrente: false,
                        horaInicio: p.start || '',
                        horaFin: p.end || ''
                    });
                }

                // Avanzamos la fecha base del ciclo completo para el siguiente slot
                currentDate.setDate(currentDate.getDate() + diasTotal);
            }

        } catch (error) {
            console.error("Error parseando patrón rotativo:", error);
        }

        return slotsTemp;
    };

    // ========== LOGICA MODAL ROTACION ==========
    
    // Helper para ordenar días
    const ordenarDiasDesdeFecha = (fecha) => {
        const diasOrdenados = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const date = new Date(fecha);
        let diaSemana = date.getDay(); 
        if (diaSemana === 0) diaSemana = 7; 
        const idxInicio = diaSemana; 
        return [
            ...diasOrdenados.slice(idxInicio),
            ...diasOrdenados.slice(0, idxInicio)
        ];
    };

    useEffect(() => {
        if (edt_fechaInicioServicio) {
            setEdt_diasOrdenadosUI(ordenarDiasDesdeFecha(edt_fechaInicioServicio));
        }
    }, [edt_fechaInicioServicio]);

    const toggleEdtServiceDay = (day) => {
        setEdt_serviceDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
    };

    const addEdtPatternRow = () => {
        setEdt_patternRows(prev => [...prev, { period: 'day', days: '', start: '', end: '', status: 'libre', dailyPeriods: [] }]);
    };
    const removeEdtPatternRow = (index) => {
        setEdt_patternRows(prev => prev.filter((_, i) => i !== index));
    };
    const handleEdtPatternRowChange = (index, field, value) => {
        setEdt_patternRows(prev => prev.map((row, i) => {
            if (i === index) {
                const updatedRow = { ...row, [field]: value };
                if (field === 'days' && updatedRow.period === 'mixto') {
                    const numDays = parseInt(value) || 0;
                    updatedRow.dailyPeriods = Array.from({ length: numDays }, (_, k) => row.dailyPeriods?.[k] || 'full');
                } else if (field === 'period' && value === 'mixto') {
                    const numDays = parseInt(row.days) || 0;
                    updatedRow.dailyPeriods = Array.from({ length: numDays }, (_, k) => row.dailyPeriods?.[k] || 'full');
                }
                return updatedRow;
            }
            return row;
        }));
    };

    const handleEdtDailyPeriodChange = (rowIndex, dayIndex, value) => {
        setEdt_patternRows(prev => prev.map((row, i) => {
            if (i === rowIndex) {
                const newDailyPeriods = [...(row.dailyPeriods || [])];
                newDailyPeriods[dayIndex] = value;
                return { ...row, dailyPeriods: newDailyPeriods };
            }
            return row;
        }));
    };

    const applyRotationTemplate = () => {
        if (!templateSelected?.data) {
            gdAlertify.notiError("Debe seleccionar una plantilla.");
            return;
        }

        try {
            const template = templateSelected.data;
            const config = typeof template.configuracion === 'string'
                ? JSON.parse(template.configuracion)
                : (template.configuracion || {});

            setEdt_patternType(template.tipo || 'fixed');
            setEdt_patternStartTime(config.horaInicio || "");
            setEdt_patternEndTime(config.horaFin || "");
            setEdt_serviceDays(Array.isArray(config.serviceDays) ? config.serviceDays : []);

            if (template.tipo === 'rotative') {
                setEdt_patternRows(Array.isArray(config.phases) ? config.phases : []);
            } else {
                setEdt_fixedPeriod(config.period || 'day');
                setEdt_patternRows([]);
            }

            gdAlertify.notiSuccess(`Plantilla "${template.titulo}" aplicada`);
        } catch (error) {
            console.error("Error applying template:", error);
            gdAlertify.notiError("No se pudo aplicar la plantilla seleccionada.");
        }
    };

    const handleOpenRotationModal = () => {
        if (!paciente) return;
        setTemplateSelected(null);
        
        // 1. Fecha Inicio
        let fechaIni = new Date().toISOString().split('T')[0];
        if (paciente.tutores) {
            const t = paciente.tutores.find(t => t.tipo_tutor === 1 || t.tipo_tutor === 3);
            if (t && t.fechaInicioServicio) fechaIni = t.fechaInicioServicio;
        }
        setEdt_fechaInicioServicio(fechaIni);

        // 2. Patrón y Días
        if (paciente.turnos) {
            setEdt_patternType(paciente.turnos.type || 'fixed');
            if (paciente.turnos.days_service) {
                setEdt_serviceDays(paciente.turnos.days_service.split(','));
            } else {
                setEdt_serviceDays([]);
            }

            if (paciente.turnos.type === 'rotative') {
                 try {
                    let parsedPattern = [];
                    if (typeof paciente.turnos.rotation_pattern === "string") {
                        const raw = JSON.parse(paciente.turnos.rotation_pattern);
                        parsedPattern = Array.isArray(raw) ? raw : [raw];
                    } else if (typeof paciente.turnos.rotation_pattern === "object") {
                        parsedPattern = [paciente.turnos.rotation_pattern];
                    }
                    if (parsedPattern && parsedPattern.length > 0) {
                        setEdt_patternRows(parsedPattern.map(p => ({
                            days: p.days || '',
                            start: p.start || '',
                            end: p.end || '',
                            period: p.period || 'day',
                            status: p.status || 'libre',
                        })));
                    } else {
                        setEdt_patternRows([]);
                    }
                } catch(e) { console.error(e); setEdt_patternRows([]); }
            } else {
                // Fixed
                setEdt_fixedPeriod(paciente.turnos.period || 'day');
                setEdt_patternStartTime(paciente.turnos.hora_inicio || '');
                setEdt_patternEndTime(paciente.turnos.hora_fin || '');
                setEdt_patternRows([]);
            }
        }
        setShowRotationModal(true);
    };

    const handleSaveRotation = async () => {
         if (!paciente) return;

         // Construir payload completo (similar a EditarPaciente)
         // Mapear datosPaciente
         const datosPaciente = {
            id: paciente.id || 0,
            rut: paciente.rut || "",
            nombres: paciente.nombres || "",
            apellidoPaterno: paciente.apellidoPaterno || "",
            apellidoMaterno: paciente.apellidoMaterno || "",
            fechaNacimiento: paciente.fechaNacimiento || "",
            direccion: paciente.direccion || "",
            comuna: paciente.comuna ? parseInt(paciente.comuna) : "",
            diagnostico: paciente.diagnostico || "",
            tipoTurno: paciente.tipoTurno || "",
            horaInicio: paciente.horaInicio || "",
            horaFin: paciente.horaFin || "",
            utilizaDatosFacturacion: paciente.datosFacturacion?.length > 0 ? 1 : 0,
            sector: paciente.sector || ""
         };

         // Mapear Tutores
         // Tutor Pago (1 o 3)
         let datosTutorPago = { utilizarTutorPaciente: 1 };
         let datosTutorPaciente = {};
         
         if (paciente.tutores) {
            const tPago = paciente.tutores.find(t => t.tipo_tutor === 1 || t.tipo_tutor === 3);
            const tSalud = paciente.tutores.find(t => t.tipo_tutor === 2 || t.tipo_tutor === 3);

            if (tPago) {
                 datosTutorPago = {
                    rut: tPago.rut || "",
                    nombres: tPago.nombres || "",
                    apellidoPaterno: tPago.apellidoPaterno || "",
                    apellidoMaterno: tPago.apellidoMaterno || "",
                    relacion: tPago.relacion || "",
                    correo: tPago.correo || "",
                    celular: tPago.celular || "",
                    // AQUI usamos la fecha nueva editada
                    fechaInicioServicio: edt_fechaInicioServicio || tPago.fechaInicioServicio || "",
                    duracionServicio: tPago.duracionServicio || "",
                    utilizarTutorPaciente: tSalud ? 0 : 1
                 };
            } else {
                 // Si no había tutor pago, metemos al menos la fecha nueva si es requerida
                 datosTutorPago.fechaInicioServicio = edt_fechaInicioServicio;
            }

            if (tSalud && tSalud.tipo_tutor !== 3) {
                 datosTutorPaciente = {
                    rut: tSalud.rut || "",
                    nombres: tSalud.nombres || "",
                    apellidoPaterno: tSalud.apellidoPaterno || "",
                    apellidoMaterno: tSalud.apellidoMaterno || "",
                    relacion: tSalud.relacion || "",
                    correo: tSalud.correo || "",
                    celular: tSalud.celular || "",
                    fechaInicioServicio: tSalud.fechaInicioServicio || "",
                    duracionServicio: tSalud.duracionServicio || ""
                 };
            }
         }

         // Mapear Valores
         let datosValoresServicios = {};
         if (paciente.valoresServicio && paciente.valoresServicio.length > 0) {
             const v = paciente.valoresServicio[0];
             datosValoresServicios = {
                 valorTurnoPaciente: v.valorTurnoPaciente || 0,
                 valorTurnoBasePaciente: v.valorTurnoBasePaciente || 0,
                 valorColacionPaciente: v.valorColacionPaciente || 0,
                 valorLocomocionPaciente: v.valorLocomocionPaciente || 0,
                 valorOtroPaciente: v.valorOtroPaciente || 0,
                 valorTurnoAuxiliar: v.valorTurnoAuxiliar || 0,
                 valorTurnoBaseAuxiliar: v.valorTurnoBaseAuxiliar || 0,
                 valorColacionAuxiliar: v.valorColacionAuxiliar || 0,
                 valorLocomocionAuxiliar: v.valorLocomocionAuxiliar || 0,
                 valorOtroAuxiliar: v.valorOtroAuxiliar || 0
             };
         }

         // Mapear Facturacion
         let datosFacturacion = {};
         if (paciente.datosFacturacion && paciente.datosFacturacion.length > 0) {
             const f = paciente.datosFacturacion[0];
             datosFacturacion = {
                razonSocial: f.razonSocial || "",
                rut: f.rut || "",
                giro: f.giro || "",
                direccion: f.direccion || "",
                comuna: f.comuna ? parseInt(f.comuna) : ""
             };
         }

         // Turnos Update
         const turnosUpdate = {
             id: paciente.turnos ? paciente.turnos.id : null,
             horaInicio: edt_patternStartTime,
             horaFin: edt_patternEndTime,
             serviceDays: edt_serviceDays,
             pattern: {
                 name: paciente.turnos ? paciente.turnos.name : "",
                 type: edt_patternType,
                 ...(edt_patternType === 'fixed'
                     ? { period: edt_fixedPeriod }
                     : { phases: edt_patternRows }
                 )
             }
         };

         // Validar campos mínimos
         if (!datosValoresServicios.valorTurnoPaciente && !datosValoresServicios.valorTurnoAuxiliar) {
              // Si no hay valores cargados, podría fallar backend validador?
              // En teoría editamos un paciente existente que ya debería tenerlos.
              // Si falla, el usuario tendrá q ir a Editar Paciente completo.
         }

         gdAlertify.confirm({
            title: 'Actualizar Rotación',
            message: '¿Confirma actualizar la rotación del paciente?',
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    const payload = {
                        datosPaciente,
                        datosTutorPago,
                        datosTutorPaciente,
                        datosValoresServicios,
                        datosFacturacion,
                        datosDias: paciente.diasTurno || [], // Mantener diasTurno si existen
                        turnos: turnosUpdate
                    };

                    const response = await axiosInstance.post("/paciente/crear", payload);
                    if (!response.data.error) {
                        gdAlertify.alertSuccess('Rotación actualizada correctamente');
                        setShowRotationModal(false);
                        handleSelectPaciente(paciente); // Usar la función principal de carga
                    } else {
                        gdAlertify.alertError(response.data.message);
                    }
                } catch (error) {
                    console.error(error);
                    gdAlertify.notiError("Error al actualizar rotación.");
                } finally {
                    gdAlertify.hideLoader();
                }
            }
         });
    };

    // ========== RECALCULAR SLOTS AL CAMBIAR FECHA ==========
    useEffect(() => {
        if (paciente && paciente.turnos) {
            const fechaReferenciaServicio = obtenerFechaInicioServicio(paciente);
            const slotsGenerados = generarSlots(paciente.turnos, fechaInicio, fechaReferenciaServicio);
            setSlots(slotsGenerados);
            setSlotsSeleccionados([]);
        }
    }, [fechaInicio, paciente]);

    // ========== SELECCIONAR AUXILIAR ==========
    const handleSelectAuxiliar = (aux) => {
        setAuxiliarActual(aux);
        setSlotsSeleccionados([]);
    };

    // ========== TOGGLE SLOT ==========
    const handleToggleSlot = (slotId) => {
        if (slotsAsignados.includes(slotId)) return;

        if (slotsSeleccionados.includes(slotId)) {
            setSlotsSeleccionados(slotsSeleccionados.filter(id => id !== slotId));
        } else {
            setSlotsSeleccionados([...slotsSeleccionados, slotId]);
        }
    };

    // ========== RECARGAR PACIENTE ==========
    const recargarPaciente = async () => {
        if (!paciente) return;

        gdAlertify.showLoader();
        try {
            const response = await axiosInstance.get("/paciente/busqueda", { params: { id: paciente.id } });
            const fullData = response.data.data[0] || {};
            setPaciente(fullData);

            // Regenerar slots
            if (fullData.turnos) {
                const fechaReferenciaServicio = obtenerFechaInicioServicio(fullData);
                const slotsGenerados = generarSlots(fullData.turnos, fechaInicio, fechaReferenciaServicio);
                setSlots(slotsGenerados);
            }

            // Limpiar selección y auxiliar
            setSlotsSeleccionados([]);
            setAuxiliarActual(null);
            setSlotsAsignados([]); // Resetear asignados porque ahora vienen del backend

            gdAlertify.notiSuccess("Paciente recargado. Listo para asignar otro auxiliar.");
        } catch (error) {
            console.error(error);
            gdAlertify.notiError("Error al recargar paciente");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    // ========== GUARDAR ASIGNACIÓN ==========
    const handleGuardar = async () => {
        if (!paciente) {
            gdAlertify.notiError("Debes seleccionar un paciente");
            return;
        }

        if (!auxiliarActual) {
            gdAlertify.notiError("Debes seleccionar un auxiliar");
            return;
        }

        if (slotsSeleccionados.length === 0) {
            gdAlertify.notiWarning("Debes seleccionar al menos un slot");
            return;
        }

        gdAlertify.confirm({
            title: "Confirmar Asignación",
            message: `¿Asignar ${slotsSeleccionados.length} slot(s) a ${auxiliarActual.nombre}?`,
            onConfirm: async () => {
                setIsSubmitting(true);
                gdAlertify.showLoader();

                try {
                    // 1. Agrupar los índices de los slots y buscar la fecha más temprana
                    const patternIndexes = [];
                    let fechaInicioMapeada = fechaInicio;

                    slotsSeleccionados.forEach(slotId => {
                        const slot = slots.find(s => s.id === slotId);
                        if (slot) {
                            patternIndexes.push(slot.patternSlotOriginal !== undefined ? slot.patternSlotOriginal : slot.slotIndex);
                        }
                    });

                    // 2. Armar el mismo payload único del modelo antiguo
                    const payload = {
                        idPaciente: paciente.id,
                        idAuxiliar: auxiliarActual.id,
                        celular: auxiliarActual.celular || '',
                        fechaInicio: fechaInicioMapeada,
                        horaInicio: paciente.horaInicio || '',
                        horaFin: paciente.horaFin || '',
                        tipoTurno: paciente.tipoTurno || 1,
                        valorTurno: valoresBase.valorTurno || 0,
                        valorColacion: valoresBase.valorColacion || 0,
                        valorMovilizacion: valoresBase.valorMovilizacion || 0,
                        valorOtro: 0,
                        tipo_colacion: valoresBase.tipoColacion || "3",
                        tipo_movilizacion: valoresBase.tipoMovilizacion || "3",
                        observacion: observacion || "",
                        patternIndex: patternIndexes.length > 0 ? patternIndexes[0] : null,
                        patternIndexes: patternIndexes,
                        inicioRealServicio: paciente.inicioRealServicio || fechaInicio
                    };

                    console.log("📤 Enviando acuerdo agrupado:", JSON.stringify(payload, null, 2));

                    const response = await axiosInstance.post("/acuerdo-mutuo/registrar", payload);
                    
                    gdAlertify.hideLoader();

                    if (!response.data.error) {
                        gdAlertify.notiSuccess("Acuerdo creado exitosamente");

                        // Marcar slots como asignados en la vista
                        setSlotsAsignados([...slotsAsignados, ...slotsSeleccionados]);

                        // Preguntar por WhatsApp
                        gdAlertify.confirm({
                            title: "Enviar Confirmaciones",
                            message: "¿Deseas enviar las confirmaciones por WhatsApp al auxiliar?",
                            onConfirm: async () => {
                                // Seguiremos mandando múltiples mensajes a WP para el detalle de cada día
                                slotsSeleccionados.forEach(slotId => {
                                    const slot = slots.find(s => s.id === slotId);
                                    if (slot && auxiliarActual && auxiliarActual.celular) {
                                        const mensaje = crearMensajeWhatsapp(slot);
                                        const phoneNumber = "56" + auxiliarActual.celular.replace(/\D/g, '');
                                        const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(mensaje)}`;
                                        window.open(whatsappUrl, '_blank');
                                    }
                                });

                                // Recargar paciente y limpiar formulario
                                await recargarPaciente();
                                setAuxiliarActual(null);
                                setSlotsSeleccionados([]);
                                setObservacion(""); // Limpiar observación
                            },
                            onCancel: async () => {
                                // Recargar paciente y limpiar formulario
                                await recargarPaciente();
                                setAuxiliarActual(null);
                                setSlotsSeleccionados([]);
                                setObservacion(""); // Limpiar observación
                            }
                        });
                    } else {
                        gdAlertify.notiError(response.data.message || "Error al registrar");
                    }

                } catch (error) {
                    gdAlertify.hideLoader();
                    gdAlertify.notiError("Error de conexión al guardar el acuerdo");
                    console.error("Error guardando acuerdo:", error);
                } finally {
                    setIsSubmitting(false);
                }
            }
        });
    };

    // ========== OBTENER ICONO DE PERIODO ==========
    const getPeriodoIcon = (periodo) => {
        if (periodo === 'day') return '☀️';
        if (periodo === 'night') return '🌙';
        if (periodo === 'mixto') return '🔄';
        return '⏰';
    };

    const getPeriodoLabel = (periodo) => {
        if (periodo === 'day') return 'Día';
        if (periodo === 'night') return 'Noche';
        if (periodo === 'mixto') return 'Mixto';
        return 'Día Y Noche';
    };

    // ========== CREAR MENSAJE WHATSAPP ==========
    const crearMensajeWhatsapp = (slot) => {
        const nombreAux = auxiliarActual?.nombre || "Auxiliar";
        const periodoLabel = getPeriodoLabel(slot.periodo);
        const recurrencia = slot.esRecurrente ? ' (Se repite semanalmente)' : '';

        const tipoColacionLabel = valoresBase.tipoColacion === "1" ? "Solo Día" :
            valoresBase.tipoColacion === "2" ? "Solo Noche" : "Día y Noche";
        const tipoMovilizacionLabel = valoresBase.tipoMovilizacion === "1" ? "Solo Día" :
            valoresBase.tipoMovilizacion === "2" ? "Solo Noche" : "Día y Noche";

        const comoLlegarTexto = paciente.como_llegar ? `\n🗺️ *Cómo llegar:* ${paciente.como_llegar}` : '';

        return `Hola ${nombreAux}, te contacto para confirmar asignación:
        
🏥 *Paciente:* ${paciente.nombres} ${paciente.apellidoPaterno || ''}
📍 *Dirección:* ${paciente.direccion || 'No especificada'}
🩺 *Patología:* ${paciente.diagnostico || 'No especificada'}${comoLlegarTexto}
📅 *Fecha Inicio:* ${formatDate(slot.fechaInicio.toISOString().split('T')[0])}${recurrencia}
🕐 *Turno:* ${periodoLabel} (${slot.dias} día${slot.dias > 1 ? 's' : ''})
⏰ *Horario:* ${slot.horaInicio || '?'} a ${slot.horaFin || '?'}

💰 *Valor del turno:* $${valoresBase.valorTurno}
🍽️ *Colación:* $${valoresBase.valorColacion} (${tipoColacionLabel})
🚗 *Movilización:* $${valoresBase.valorMovilizacion} (${tipoMovilizacionLabel})

Por favor confirma tu asistencia. 🙌`;
    };

    // ========== GENERAR PROYECCIÓN DE 15 DÍAS (DESDE BD) ==========
    const [proyeccionData, setProyeccionData] = useState([]);
    const [loadingProyeccion, setLoadingProyeccion] = useState(false);

    const cargarProyeccionReal = async () => {
        if (!paciente) return;

        setLoadingProyeccion(true);
        try {
            // Calcular 2 ciclos completos del patrón
            let diasProyeccion = 15; // Default

            if (paciente.turnos && paciente.turnos.type === "rotative") {
                try {
                    let pattern = typeof paciente.turnos.rotation_pattern === "string"
                        ? JSON.parse(paciente.turnos.rotation_pattern)
                        : paciente.turnos.rotation_pattern;

                    if (!Array.isArray(pattern)) pattern = [pattern];

                    // Calcular días de un ciclo completo
                    const diasPorCiclo = pattern.reduce((sum, p) => sum + (parseInt(p.days) || 0), 0);
                    diasProyeccion = diasPorCiclo * 2; // 2 ciclos completos
                } catch (error) {
                    console.error("Error calculando ciclos:", error);
                }
            }

            // Usamos T12:00:00 para evitar problemas de timezone
            const fechaIni = new Date(fechaInicio + 'T12:00:00');
            const fechaFin = new Date(fechaInicio + 'T12:00:00');
            fechaFin.setDate(fechaFin.getDate() + diasProyeccion - 1);

            try {
                const response = await axiosInstance.get("/turnos/proyeccion", {
                    params: {
                        pacienteId: paciente.id,
                        fechaInicio: fechaIni.toISOString().split('T')[0],
                        fechaFin: fechaFin.toISOString().split('T')[0]
                    }
                });

                if (!response.data.error && response.data.data) {
                    setProyeccionData(response.data.data);
                } else {
                    generarProyeccionTeorica(diasProyeccion);
                }
            } catch (err) {
                console.warn("Error consultando proyeccion backend, usando fallback local", err);
                generarProyeccionTeorica(diasProyeccion);
            }
        } catch (error) {
            console.error("Error cargando proyección:", error);
            // Fallback: generar teóricamente
            generarProyeccionTeorica();
        } finally {
            setLoadingProyeccion(false);
        }
    };

    const generarProyeccionTeorica = (diasProyeccion = 15) => {
        if (!paciente || !paciente.turnos) {
            setProyeccionData([]);
            return;
        }

        const proyeccion = [];
        const fechaReferenciaServicio = obtenerFechaInicioServicio(paciente);

        if (paciente.turnos.type === "rotative") {
            try {
                let pattern = typeof paciente.turnos.rotation_pattern === "string"
                    ? JSON.parse(paciente.turnos.rotation_pattern)
                    : paciente.turnos.rotation_pattern;

                if (!Array.isArray(pattern)) pattern = [pattern];

                const cicloCompleto = pattern.reduce((sum, p) => sum + (parseInt(p.days) || 0), 0);
                const fechaRef = new Date(fechaReferenciaServicio + 'T12:00:00');
                const fechaIni = new Date(fechaInicio + 'T12:00:00');
                const diasDesdeReferencia = Math.floor((fechaIni - fechaRef) / (1000 * 60 * 60 * 24));
                const posicionEnCiclo = ((diasDesdeReferencia % cicloCompleto) + cicloCompleto) % cicloCompleto;

                let diasAcumulados = 0;
                let slotInicial = 0;
                let diasDentroDelSlot = 0;

                for (let i = 0; i < pattern.length; i++) {
                    const diasDelSlot = parseInt(pattern[i].days) || 0;
                    if (diasAcumulados + diasDelSlot > posicionEnCiclo) {
                        slotInicial = i;
                        diasDentroDelSlot = posicionEnCiclo - diasAcumulados;
                        break;
                    }
                    diasAcumulados += diasDelSlot;
                }

                let currentDate = new Date(fechaIni);
                let slotIndex = slotInicial;
                let diasRestantesEnSlotActual = (parseInt(pattern[slotInicial].days) || 0) - diasDentroDelSlot;

                for (let dia = 0; dia < diasProyeccion; dia++) {
                    const p = pattern[slotIndex % pattern.length];
                    const diaSemana = new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(currentDate);
                    const fechaStr = currentDate.toISOString().split('T')[0];

                    // Definir regla específica para el día si es mixto
                    let reglaDelDiaActual = p.period;
                    if (p.period === 'mixto' && p.dailyPeriods) {
                        const totalDaysFase = parseInt(p.days) || 0;
                        const diaDentroDeFase = totalDaysFase - diasRestantesEnSlotActual;
                        reglaDelDiaActual = p.dailyPeriods[diaDentroDeFase] || 'full';
                    }

                    // Definir si corresponde día/noche según la regla del día actual
                    const correspondeDia = reglaDelDiaActual === 'day' || reglaDelDiaActual === 'full';
                    const correspondeNoche = reglaDelDiaActual === 'night' || reglaDelDiaActual === 'full';

                    // SIEMPRE generar registro de DÍA (1)
                    proyeccion.push({
                        fecha: fechaStr,
                        diaSemana: diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1),
                        turno: "1", // Día
                        status: correspondeDia ? (p.status || 'libre') : 'libre',
                        auxiliar: null,
                        auxiliarNombre: null,
                        esTeorico: true
                    });

                    // SIEMPRE generar registro de NOCHE (2)
                    proyeccion.push({
                        fecha: fechaStr,
                        diaSemana: diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1),
                        turno: "2", // Noche
                        status: correspondeNoche ? (p.status || 'libre') : 'libre',
                        auxiliar: null,
                        auxiliarNombre: null,
                        esTeorico: true
                    });

                    diasRestantesEnSlotActual--;
                    if (diasRestantesEnSlotActual <= 0) {
                        slotIndex++;
                        const nextP = pattern[slotIndex % pattern.length];
                        diasRestantesEnSlotActual = parseInt(nextP.days) || 0;
                    }

                    currentDate.setDate(currentDate.getDate() + 1);
                }
            } catch (error) {
                console.error("Error generando proyección:", error);
            }
        } else {
            // Patrón fijo
            const daysService = paciente.turnos.days_service ? paciente.turnos.days_service.split(',') : [];
            const dayMap = { 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6, 'Sun': 0 };
            const periodo = paciente.turnos.period || 'day';
            const correspondeDia = periodo === 'day' || periodo === 'full';
            const correspondeNoche = periodo === 'night' || periodo === 'full';

            let currentDate = new Date(fechaInicio + 'T12:00:00');
            for (let dia = 0; dia < diasProyeccion; dia++) {
                const dayOfWeek = currentDate.getDay();
                const dayCode = Object.keys(dayMap).find(key => dayMap[key] === dayOfWeek);
                const esDiaServicio = daysService.includes(dayCode);
                const diaSemana = new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(currentDate);
                const fechaStr = currentDate.toISOString().split('T')[0];

                // SIEMPRE generar registro de DÍA (1)
                proyeccion.push({
                    fecha: fechaStr,
                    diaSemana: diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1),
                    turno: "1", // Día
                    status: (esDiaServicio && correspondeDia) ? 'worked' : 'libre',
                    auxiliar: null,
                    auxiliarNombre: null,
                    esTeorico: true
                });

                // SIEMPRE generar registro de NOCHE (2)
                proyeccion.push({
                    fecha: fechaStr,
                    diaSemana: diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1),
                    turno: "2", // Noche
                    status: (esDiaServicio && correspondeNoche) ? 'worked' : 'libre',
                    auxiliar: null,
                    auxiliarNombre: null,
                    esTeorico: true
                });

                currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        setProyeccionData(proyeccion);
    };

    // ========== RENDERIZADO ==========
    return (
        <div className="container-fluid mt-4" style={{ maxWidth: '1400px' }}>
            {/* PASO 1: SELECCIONAR PACIENTE */}
            <div className="card shadow-sm mb-3">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <span className="badge bg-light text-primary me-2">1</span>
                        Seleccionar Paciente y Fecha de Inicio
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <BuscarPaciente onSelect={handleSelectPaciente} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-bold">Fecha Inicio Acuerdo (Inicio Rotación)</label>
                            <input
                                type="date"
                                className="form-control"
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                                readOnly={true}
                            />
                            <small className="text-danger mt-1 d-block" style={{ fontSize: '0.7rem' }}>
                                * Fecha inicio del acuerdo es la fecha de inicio de la rotación, que se modifica en actualizar rotación.
                            </small>
                        </div>
                        <div className="col-md-3">
                            {paciente && (
                                <>
                                    <label className="form-label small fw-bold">Slots Disponibles</label>
                                    <div className="alert alert-info mb-0 py-2 text-center">
                                        <strong>{slots.length}</strong> slots
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Valores Base */}
                    {paciente && (
                        <div className="row g-2 mt-2">
                            <div className="col-md-3">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">Turno</span>
                                    <input type="number" className="form-control"
                                        value={valoresBase.valorTurno}
                                        onChange={(e) => setValoresBase({ ...valoresBase, valorTurno: e.target.value })} />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">Colación</span>
                                    <input type="number" className="form-control"
                                        placeholder="Valor"
                                        value={valoresBase.valorColacion}
                                        onChange={(e) => setValoresBase({ ...valoresBase, valorColacion: e.target.value })} />
                                    <select
                                        className="form-select"
                                        style={{ maxWidth: '130px' }}
                                        value={valoresBase.tipoColacion}
                                        onChange={(e) => setValoresBase({ ...valoresBase, tipoColacion: e.target.value })}
                                    >
                                        <option value="1">Día</option>
                                        <option value="2">Noche</option>
                                        <option value="3">Ambos</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-3">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">Movilización</span>
                                    <input type="number" className="form-control"
                                        placeholder="Valor"
                                        value={valoresBase.valorMovilizacion}
                                        onChange={(e) => setValoresBase({ ...valoresBase, valorMovilizacion: e.target.value })} />
                                    {/* Select comentado temporalmente */}
                                    {/* <select
                                        className="form-select"
                                        style={{ maxWidth: '100px' }}
                                        value={valoresBase.tipoMovilizacion}
                                        onChange={(e) => setValoresBase({ ...valoresBase, tipoMovilizacion: e.target.value })}
                                    >
                                        <option value="1">Día</option>
                                        <option value="2">Noche</option>
                                        <option value="3">Ambos</option>
                                    </select> */}
                                </div>
                            </div>

                            <div className="col-md-3">
                                <button
                                    className="btn btn-warning btn-sm w-100"
                                    onClick={handleOpenRotationModal}
                                    title="Actualizar configuración de rotación (Días, Patrón, Horarios)"
                                    disabled={slotsAsignados.length > 0 || slots.some(s => s.status === 'ocupado')}
                                >
                                    <i className="bi bi-pencil-square me-2"></i>
                                    Actualizar Rotación
                                </button>
                                {(slotsAsignados.length > 0 || slots.some(s => s.status === 'ocupado')) && (
                                    <div className="text-danger small mt-1 text-center" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        No se puede actualizar rotación con acuerdos mutuos abiertos
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {paciente && (
                <>
                    {/* PASO 2: SELECCIONAR AUXILIAR */}
                    <div className="card shadow-sm mb-3">
                        <div className="card-header bg-success text-white">
                            <h5 className="mb-0">
                                <span className="badge bg-light text-success me-2">2</span>
                                Asignar Auxiliar
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row align-items-center">
                                <div className="col-md-8">
                                    <BuscarAuxiliar onSelect={handleSelectAuxiliar} />
                                </div>
                                <div className="col-md-4">
                                    {auxiliarActual && (
                                        <div className="alert alert-success mb-0 py-2">
                                            <i className="bi bi-person-check me-2"></i>
                                            <strong>{auxiliarActual.nombre}</strong>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* PASO 3: SELECCIONAR SLOTS */}
                    <div className="card shadow-sm mb-3">
                        <div className="card-header bg-info text-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <span className="badge bg-light text-info me-2">3</span>
                                    Asignar Slots
                                </h5>
                                <span className="badge bg-light text-dark">
                                    {slotsSeleccionados.length} seleccionado(s)
                                </span>
                            </div>
                        </div>
                        <div className="card-body">
                            {slots.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
                                    <p className="mt-2 mb-0">No hay slots disponibles</p>
                                    <small>El patrón puede estar marcado como "libre"</small>
                                </div>
                            ) : (
                                <div className="row g-2">
                                    {slots.map((slot) => {
                                        const estaSeleccionado = slotsSeleccionados.includes(slot.id);
                                        const estaAsignado = slotsAsignados.includes(slot.id);
                                        const estaOcupado = slot.status === 'ocupado'; // NUEVO: verificar si está ocupado
                                        const noDisponible = estaAsignado || estaOcupado;

                                        let cardClass = "card h-100 slot-card";
                                        if (noDisponible) {
                                            cardClass += " border-danger bg-danger bg-opacity-10";
                                        } else if (estaSeleccionado) {
                                            cardClass += " border-success bg-success bg-opacity-10";
                                        } else {
                                            cardClass += " border-primary";
                                        }

                                        return (
                                            <div key={slot.id} className="col-md-3 mb-3">
                                                <div
                                                    className={cardClass}
                                                    style={{
                                                        cursor: noDisponible ? 'not-allowed' : 'pointer',
                                                        opacity: noDisponible ? 0.6 : 1
                                                    }}
                                                    onClick={() => !noDisponible && handleToggleSlot(slot.id)}
                                                >
                                                    <div className="card-body p-3">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <small className="text-muted">Slot #{slot.id + 1}</small>
                                                                {slot.esRecurrente && (
                                                                    <span className="badge bg-info">
                                                                        <i className="bi bi-arrow-repeat"></i>
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div>
                                                                {noDisponible ? (
                                                                    <i className="bi bi-x-circle-fill text-danger fs-5"></i>
                                                                ) : estaSeleccionado ? (
                                                                    <i className="bi bi-check-circle-fill text-success fs-5"></i>
                                                                ) : (
                                                                    <i className="bi bi-circle text-muted fs-5"></i>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <h6 className="mb-1 fw-bold">
                                                            {getPeriodoIcon(slot.periodo)} {slot.diaSemana}
                                                        </h6>
                                                        <p className="mb-1 small text-muted">
                                                            <i className="bi bi-calendar-event me-1"></i>
                                                            {formatDate(slot.fechaInicio.toISOString().split('T')[0])}
                                                        </p>
                                                        <div className="small">
                                                            <span className="badge bg-light text-dark border">
                                                                {slot.dias} {slot.dias === 1 ? 'día' : 'días'}
                                                            </span>
                                                            <span className="ms-2 badge bg-light text-dark border">
                                                                {getPeriodoLabel(slot.periodo)}
                                                            </span>
                                                        </div>
                                                        {slot.esRecortado && (
                                                            <div className="mt-1 text-muted" style={{ fontSize: '0.75rem' }}>
                                                                <i className="bi bi-info-circle me-1"></i>
                                                                Real: {slot.originalDias} días
                                                            </div>
                                                        )}
                                                        <div className="mt-2 text-muted small">
                                                            <i className="bi bi-clock me-1"></i>
                                                            {slot.horaInicio} - {slot.horaFin}
                                                        </div>
                                                        {noDisponible && (
                                                            <div className="mt-2">
                                                                <span className="badge bg-danger w-100">
                                                                    {estaOcupado ? 'Ocupado' : 'No disponible'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* PASO 4: GUARDAR */}
                    <div className="card shadow-sm">
                        <div className="card-body p-3">
                            <div className="mb-3">
                                <label className="form-label small fw-bold">Observación (Opcional)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ej: Reemplazo por licencia, refuerzo, etc."
                                    value={observacion}
                                    onChange={(e) => setObservacion(e.target.value)}
                                />
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="mb-0">
                                        <i className="bi bi-info-circle me-2"></i>
                                        {slotsAsignados.length > 0 && (
                                            <span className="text-danger">
                                                {slotsAsignados.length} slot(s) ya asignado(s) |
                                            </span>
                                        )}
                                        <span className="text-success ms-1">
                                            {slotsSeleccionados.length} slot(s) seleccionado(s)
                                        </span>
                                    </h6>
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-outline-info"
                                        onClick={() => {
                                            setShowProyeccion(true);
                                            cargarProyeccionReal();
                                        }}
                                        disabled={!paciente}
                                    >
                                        <i className="bi bi-calendar3 me-2"></i>
                                        Visualizar Proyección
                                    </button>
                                    <button
                                        className="btn btn-primary btn-lg"
                                        onClick={handleGuardar}
                                        disabled={isSubmitting || !auxiliarActual || slotsSeleccionados.length === 0}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-save me-2"></i>
                                                Guardar Asignación
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {!paciente && (
                <div className="text-center py-5">
                    <div className="card shadow-sm bg-light">
                        <div className="card-body py-5">
                            <i className="bi bi-calendar-heart text-muted" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                            <h3 className="mt-3 text-muted">Selecciona un Paciente</h3>
                            <p className="text-muted mb-0">Los slots se mostrarán según el patrón de turnos</p>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE PROYECCIÓN */}
            {showProyeccion && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-calendar3 me-2"></i>
                                    Proyección - 2 Ciclos Completos
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowProyeccion(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Vista de cómo quedaría el patrón desde <strong>{formatDate(fechaInicio)}</strong>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Día</th>
                                                <th>Fecha</th>
                                                <th className="text-center">Turno</th>
                                                <th>Estado</th>
                                                <th>Asignación</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadingProyeccion ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        <div className="spinner-border spinner-border-sm me-2"></div>
                                                        Cargando proyección...
                                                    </td>
                                                </tr>
                                            ) : proyeccionData.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4 text-muted">
                                                        No hay datos de proyección
                                                    </td>
                                                </tr>
                                            ) : (
                                                proyeccionData.map((registro, index) => {
                                                    // registro tiene: fecha, turno (1=día, 2=noche), auxiliar, auxiliarNombre, status
                                                    const esDia = registro.turno === "1" || registro.turno === 1;
                                                    const esNoche = registro.turno === "2" || registro.turno === 2;
                                                    const esTrabajo = registro.status !== 'libre';

                                                    // Buscar si este turno específico corresponde a un slot seleccionado
                                                    const slotCorrespondiente = slots.find(slot => {
                                                        const slotDate = slot.fechaInicio.toISOString().split('T')[0];
                                                        const registroDate = typeof registro.fecha === 'string' ? registro.fecha : registro.fecha.toISOString().split('T')[0];
                                                        const slotEndDate = slot.fechaFin.toISOString().split('T')[0];

                                                        const fechaCoincide = registroDate >= slotDate && registroDate <= slotEndDate;
                                                        const periodoCoincide =
                                                            slot.periodo === 'full' ||
                                                            (slot.periodo === 'day' && esDia) ||
                                                            (slot.periodo === 'night' && esNoche);

                                                        return fechaCoincide && periodoCoincide;
                                                    });

                                                    const estaSeleccionado = slotCorrespondiente && slotsSeleccionados.includes(slotCorrespondiente.id);
                                                    const estaAsignado = slotCorrespondiente && slotsAsignados.includes(slotCorrespondiente.id);

                                                    // Si viene del backend con auxiliar
                                                    const tieneAuxiliar = registro.auxiliar && registro.auxiliarNombre;

                                                    let rowClass = '';
                                                    let estadoBadge = null;
                                                    let asignacionInfo = '-';

                                                    if (tieneAuxiliar) {
                                                        // Datos reales del backend
                                                        rowClass = 'table-warning';
                                                        estadoBadge = <span className="badge bg-warning text-dark">Asignado (BD)</span>;
                                                        asignacionInfo = (
                                                            <span className="text-dark">
                                                                <i className="bi bi-person-fill me-1"></i>
                                                                {registro.auxiliarNombre}
                                                            </span>
                                                        );
                                                    } else if (estaAsignado) {
                                                        rowClass = 'table-danger';
                                                        estadoBadge = <span className="badge bg-danger">Asignado</span>;
                                                        asignacionInfo = <span className="text-danger"><i className="bi bi-x-circle-fill me-1"></i>Bloqueado</span>;
                                                    } else if (estaSeleccionado) {
                                                        rowClass = 'table-success';
                                                        estadoBadge = <span className="badge bg-success">Seleccionado</span>;
                                                        asignacionInfo = auxiliarActual ? (
                                                            <span className="text-success">
                                                                <i className="bi bi-person-check me-1"></i>
                                                                {auxiliarActual.nombre}
                                                            </span>
                                                        ) : '-';
                                                    } else if (registro.status === 'ocupado') {
                                                        rowClass = 'table-danger bg-opacity-10'; // Rojo suave
                                                        estadoBadge = <span className="badge bg-danger">Ocupado</span>;
                                                        asignacionInfo = <span className="text-danger small"><i className="bi bi-slash-circle me-1"></i>No disponible</span>;
                                                    } else if (esTrabajo) {
                                                        rowClass = 'table-info';
                                                        estadoBadge = <span className="badge bg-info">Disponible</span>;
                                                        asignacionInfo = <span className="text-muted">Sin asignar</span>;
                                                    } else {
                                                        estadoBadge = <span className="badge bg-secondary">Libre</span>;
                                                    }

                                                    const fechaFormateada = typeof registro.fecha === 'string' ? registro.fecha : registro.fecha.toISOString().split('T')[0];
                                                    const turnoLabel = esDia ? 'Día' : 'Noche';

                                                    return (
                                                        <tr key={index} className={rowClass}>
                                                            <td><strong>{registro.diaSemana}</strong></td>
                                                            <td>{formatDate(fechaFormateada)}</td>
                                                            <td className="text-center">
                                                                <span className={esDia ? 'text-primary' : 'text-danger'}>
                                                                    {turnoLabel}
                                                                </span>
                                                            </td>
                                                            <td>{estadoBadge}</td>
                                                            <td>{asignacionInfo}</td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Leyenda */}
                                <div className="alert alert-light border mt-3">
                                    <h6 className="mb-2"><i className="bi bi-info-circle me-2"></i>Leyenda:</h6>
                                    <div className="row g-2 small">
                                        <div className="col-md-3">
                                            <span className="badge bg-warning text-dark me-1">Asignado (BD)</span> En base de datos
                                        </div>
                                        <div className="col-md-2">
                                            <span className="badge bg-danger me-1">Asignado</span> Recién guardado
                                        </div>
                                        <div className="col-md-3">
                                            <span className="badge bg-success me-1">Seleccionado</span> Marcado para guardar
                                        </div>
                                        <div className="col-md-2">
                                            <span className="badge bg-info me-1">Disponible</span> Listo para asignar
                                        </div>
                                        <div className="col-md-2">
                                            <span className="badge bg-secondary me-1">Libre</span> Día sin turno
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowProyeccion(false)}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE ROTACION */}
            {showRotationModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-warning text-dark">
                                <h5 className="modal-title">
                                    <i className="bi bi-pencil-square me-2"></i>
                                    Actualizar Rotación
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowRotationModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-8">
                                        <label className="form-label fw-bold font-primary">Cargar desde Plantilla (Opcional)</label>
                                        <Select
                                            options={patronesTemplates}
                                            placeholder="Seleccionar un patrón guardado..."
                                            isClearable
                                            value={templateSelected}
                                            onChange={setTemplateSelected}
                                        />
                                    </div>
                                    <div className="col-md-4 d-flex align-items-end">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary w-100"
                                            onClick={applyRotationTemplate}
                                            disabled={!templateSelected}
                                        >
                                            Aplicar
                                        </button>
                                    </div>
                                </div>
                                <hr />
                                <fieldset className="border p-3 mb-3">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Fecha de Inicio Rotación</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={edt_fechaInicioServicio}
                                            onChange={(e) => setEdt_fechaInicioServicio(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <legend className="w-auto">Patrón</legend>
                                    <div className="form-group">
                                        <label>Días de turno</label><br />
                                    <div className="col-md-12 mb-3 row">
                                            {edt_diasOrdenadosUI.map(code => {
                                                const day = daysMap.find(d => d.value === code);
                                                return (
                                                    <div key={day.value} className="col text-center">
                                                        <label className="form-label d-block text-truncate" title={day.label} htmlFor={`edt-${day.value}`}>
                                                            {day.label}
                                                        </label>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`edt-svc-${day.value}`}
                                                            value={day.value}
                                                            checked={edt_serviceDays.includes(day.value)}
                                                            onChange={() => toggleEdtServiceDay(day.value)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <hr />
                                    <h4>Patrón de turno</h4>
                                    <div className="form-group mb-3">
                                        <label>Tipo</label><br />
                                        <div className="custom-control custom-radio custom-control-inline me-3 d-inline-block">
                                            <input
                                                type="radio"
                                                id="edt-fixed"
                                                name="edt-patternType"
                                                value="fixed"
                                                className="form-check-input me-1"
                                                checked={edt_patternType === 'fixed'}
                                                onChange={() => setEdt_patternType('fixed')}
                                            />
                                            <label className="custom-control-label" htmlFor="edt-fixed">Fijo</label>
                                        </div>
                                        <div className="custom-control custom-radio custom-control-inline d-inline-block">
                                            <input
                                                type="radio"
                                                id="edt-rotative"
                                                name="edt-patternType"
                                                value="rotative"
                                                className="form-check-input me-1"
                                                checked={edt_patternType === 'rotative'}
                                                onChange={() => setEdt_patternType('rotative')}
                                            />
                                            <label className="custom-control-label" htmlFor="edt-rotative">Rotativo</label>
                                        </div>
                                    </div>

                                    {edt_patternType === 'fixed' ? (
                                        <div className="form-group border p-3 bg-light rounded" id="edt-fixed-options">
                                            <label>Periodo Fijo</label>
                                            <select
                                                className="form-control mb-3"
                                                value={edt_fixedPeriod}
                                                onChange={e => setEdt_fixedPeriod(e.target.value)}
                                            >
                                                <option value="day">Sólo Día</option>
                                                <option value="night">Sólo Noche</option>
                                                <option value="full">Día y Noche</option>
                                            </select>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label>Hora Inicio Turno</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        value={edt_patternStartTime}
                                                        onChange={e => setEdt_patternStartTime(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label>Hora Fin Turno</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        value={edt_patternEndTime}
                                                        onChange={e => setEdt_patternEndTime(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="form-group border p-3 bg-light rounded" id="edt-rotative-options">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                 <label>Patrón de Rotación</label>
                                                 <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={addEdtPatternRow}
                                                >
                                                    <i className="bi bi-plus-circle me-1"></i> Añadir Fase
                                                </button>
                                            </div>
                                           
                                            <div id="edt-pattern-container">
                                                {edt_patternRows.map((row, idx) => (
                                                    <div key={idx} className="pattern-row row g-2 mb-2 p-2 border-bottom">
                                                        <div className="col-md-3">
                                                            <label className="small">Tipo</label>
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={row.period}
                                                                onChange={e => handleEdtPatternRowChange(idx, 'period', e.target.value)}
                                                            >
                                                                <option value="day">Día</option>
                                                                <option value="night">Noche</option>
                                                                <option value="full">Día y Noche</option>
                                                                <option value="mixto">Mixto</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <label className="small">Días</label>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                min="1"
                                                                value={row.days}
                                                                onChange={e => handleEdtPatternRowChange(idx, 'days', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-md-2">
                                                            <label className="small">Inicio</label>
                                                            <input
                                                                type="time"
                                                                className="form-control form-control-sm"
                                                                value={row.start}
                                                                onChange={e => handleEdtPatternRowChange(idx, 'start', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-md-2">
                                                            <label className="small">Fin</label>
                                                            <input
                                                                type="time"
                                                                className="form-control form-control-sm"
                                                                value={row.end}
                                                                onChange={e => handleEdtPatternRowChange(idx, 'end', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-md-3 d-flex align-items-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger w-100"
                                                                onClick={() => removeEdtPatternRow(idx)}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                        {row.period === 'mixto' && (
                                                            <div className="col-12 mt-1">
                                                                <div className="d-flex flex-wrap gap-2 align-items-center bg-light px-2 py-1 rounded" style={{ borderLeft: '3px solid #0d6efd' }}>
                                                                    <span className="small fw-bold text-primary me-2"><i className="bi bi-gear-fill me-1"></i>Mixto:</span>
                                                                    {Array.from({ length: parseInt(row.days) || 0 }).map((_, dayIdx) => (
                                                                        <div key={dayIdx} className="d-flex align-items-center mb-1 mt-1">
                                                                            <span className="small text-muted me-1" style={{fontSize: '0.75rem'}}>D{dayIdx + 1}:</span>
                                                                            <select
                                                                                className="form-control form-control-sm border-primary"
                                                                                style={{ width: '100px', fontSize: '0.75rem', padding: '0.1rem 0.5rem' }}
                                                                                value={row.dailyPeriods?.[dayIdx] || 'full'}
                                                                                onChange={e => handleEdtDailyPeriodChange(idx, dayIdx, e.target.value)}
                                                                            >
                                                                                <option value="full">Día/Noche</option>
                                                                                <option value="day">Día</option>
                                                                                <option value="night">Noche</option>
                                                                                <option value="libre">Libre</option>
                                                                            </select>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </fieldset>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowRotationModal(false)}>Cancelar</button>
                                <button type="button" className="btn btn-primary" onClick={handleSaveRotation}>Guardar Cambios</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NuevoAcuerdoMutuo2;
